// eslint-disable-next-line no-unused-vars
const productFieldObject = require('../../datalayer/models/productFieldObject'); // Used for JSDoc

const getAnalyticsEventCenter = require('../../../common/getAnalyticsEventCenter');
const getBaseConfig = require('../../../common/getBaseConfig');
const { loadScript } = require('../../../utilities/dom/loadScript');
const { getCanonicalProductId } = require('../../../utilities/doubleLookHelpers');
const { getContactKeyForEmailAddress } = require('../../../utilities/sfmcHelpers');
const { getCustomerContactKey, isAnonymousCustomer } = require('./session');

/**
 * Global tracking object from collect.js for Salesforce Marketing Cloud (SFMC).
 *
 * Official documentation:
 * - Overview: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_collect_code.htm
 * - doNotTrack: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_do_not_track.htm
 * - setUserInfo: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_set_user_info.htm
 * - trackPageView: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_track_page_view.htm
 * - trackCart: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_track_cart.htm
 * - trackConversion: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_track_conversion.htm
 * - trackWishlist: https://help.salesforce.com/s/articleView?id=sf.mc_ctc_configure_trackwishlist.htm
 *
 * @typedef {Object} ETMC
 * @property {function():void} doNotTrack - Disables tracking on the page.
 * @property {function(string):void} setOrgId - Sets your business unit's MID.
 * @property {function(Object):void} setUserInfo - Sends user data (e.g. {email, details}).
 * @property {function(string):void} setFirstParty - Sets a custom first-party endpoint.
 * @property {function():void} setInsecure - Enables tracking over HTTP.
 * @property {function(...any[]):void} callFunc - Calls internal collect.js functions.
 * @property {function(any[]):void} setup - Initializes the collect.js library.
 * @property {function(any):void} push - Queues a command before full load.
 * @property {function():boolean} isEtConversionTracking - Returns true if enabled.
 * @property {function():string} getCurrentLocation - Returns current page URL.
 * @property {function(Object):void} trackPageView - Tracks a page view event.
 * @property {function(Object):void} trackCart - Sends cart update (e.g. {cart: [...]})
 * @property {function(Object):void} trackConversion - Tracks a conversion or purchase.
 * @property {function(Object):void} trackEvent - Sends a custom event with details.
 * @property {function(Object):void} trackWishlist - Tracks wishlist actions.
 * @property {function(Object):void} trackRating - Tracks product rating by users.
 * @property {function(Object):void} updateItem - Updates item catalog data.
 * @property {function(string, Object):void} sendDataWithImage - Sends tracking via pixel.
 * @property {function(Object):Object} standardizeCart - Normalizes cart data format.
 * @property {function(Object):void} setDefaultItemValues - Sets default item values.
 * @property {function(string):string} getURLParameter - Gets URL query parameter.
 * @property {function(Object):boolean} hasRequiredFields - Validates item fields.
 * @property {function(string):boolean} shouldValidateName - Validates item name.
 */

/**
 * @type {Promise<ETMC>}
 */
// eslint-disable-next-line no-underscore-dangle
let _etmc;
const analyticsEventCenter = getAnalyticsEventCenter();
const BASE_CONFIG = getBaseConfig();
const MID = BASE_CONFIG.sfmc.mid;

/**
 * Manage a cart duplicate to be used when the user turns into a known one
 * (reveals its email). Then we can use the info to force a trackCart event.
 */
const anonymousCart = {
    key: 'collectjs-session-cart',
    /**
     * Returns a previously-saved object with cart-related info to push
     * a trackCart event that do not have it (ex: login)
     * @returns {Object} Cart info
     */
    get() {
        let cart = sessionStorage.getItem(this.key);
        cart = cart ? JSON.parse(cart) : null;
        return cart;
    },
    /**
     * Deletes the previously-saved object
     */
    remove() {
        sessionStorage.removeItem(this.key);
    },
    /**
     * Saves a object with cart-related info into a temporal storage to use it
     * to push trackCart events that do not have that info (ex: login)
     * @param {Object} cart - Cart info
     * @param {ProductFieldObject.ProductFieldObject[]} cart.items - The
     * product field objects of all the items in cart
     */
    set(cart) {
        sessionStorage.setItem(this.key, JSON.stringify(cart));
    }
};

/**
 * Send event to SFMC Collect.js with the org and the corresponding user.
 *
 * @param {string} eventType ’track_page_view’, ’track_cart’, etc.
 * @param {Object} eventData { item, title, url, ... }
 * @param {Object} [options] Optional params
 * @param {string} [options.uniqueId] Force to use this uniqueId. Otherwise call
 *  getUserUniqueIdentifier() as default way.
 */
async function pushEvent(eventType, eventData, options = {}) {
    /**
     * @type {string|null}
     * The unique identifier of the user for collect.js. It shouldn't be the
     * email (eg: Contact Key)
     */
    let uniqueId = options.uniqueId;
    if (!uniqueId) {
        uniqueId = getCustomerContactKey();
    }

    _etmc = await _etmc;

    _etmc.push(['setOrgId', MID]);

    if (uniqueId) {
        _etmc.push(['setUserInfo', { email: uniqueId }]);
    }

    _etmc.push([eventType, eventData]);
}

/**
 * Loads the IgoDigital collect.js script for a given MID.
 * @param {string} mid The Marketing Cloud MID.
 * @returns {Promise<ETMC>} Resolves when the script is loaded.
 */
function loadCollectJs(mid) {
    if (!mid) {
        return Promise.reject(new Error('MID is required'));
    }
    // <script type="text/javascript" src="MID.collect.igodigital.com/collect.js"></script>
    const src = `https://${mid}.collect.igodigital.com/collect.js`;
    let scriptPromise = loadScript(src);
    return scriptPromise.then(
        () => {
            // eslint-disable-next-line no-underscore-dangle
            window._etmc = window._etmc || [];
            const { _etmc: globalEtmc } = window;
            // globalEtmc.debug = true;
            return globalEtmc;
        }
    );
}

/**
 * Parse ProductFieldObject to collect js model for the item of cart
 * @param {productFieldObject.ProductFieldObject} pfo item
 * @returns {Object} item for trackCart event
 */
function toCollectCartItem(pfo) {
    const variationGroupId = getCanonicalProductId(
        pfo.addons.adDatalayerProductId
    );
    return {
        item: variationGroupId,
        quantity: pfo.quantity,
        price: pfo.price,
        unique_id: pfo.content_ids // Variant ID as unique
    };
}

/**
 * Handle cart related event (add or remove a product)
 * @param {Object} payload The payload of the event
 * @param {Object} [options] Optional params
 * @param {string} [options.userUniqueId] Force using this uniqueId
 */
async function handleCartEvent(payload, options) {
    /** @type {productFieldObject.ProductFieldObject[]} */
    const cartItems = payload?.cart?.items ?? [];
    const mustUpdateAnonymousCart = isAnonymousCustomer();
    if (cartItems.length > 0) {
        pushEvent('trackCart', {
            cart: cartItems.map(toCollectCartItem)
        }, options);
        if (mustUpdateAnonymousCart) anonymousCart.set(payload.cart);
    } else {
        pushEvent('trackCart', {
            clear_cart: true
        }, options);
        if (mustUpdateAnonymousCart) anonymousCart.remove();
    }
}

/**
 * Handle cart related event (add or remove a product)
 * @param {Object} payload The payload of the event
 */
async function handlePageviewLoadedEvent({
    pid,
    categoryName,
    searchKeywords
} = {}) {
    if (pid) {
        pushEvent('trackPageView', { item: pid });
    } else if (categoryName) {
        pushEvent('trackPageView', { category: categoryName });
    } else if (searchKeywords) {
        pushEvent('trackPageView', { search: searchKeywords });
    }
}

/**
 * Handle purchased event
 *
 * @param {Object} payload Event useful info
 * @param {string} payload.allCouponsApplied The coupons applied to each
 * item of the order
 * @param {string} payload.countryCode The country code of the purchase
 * @param {string} payload.currencyCode The current currency code
 * @param {ProductFieldObject.ProductFieldObject[]} payload.items Product
 * field objects of all the items in cart
 * @param {string} payload.grandTotal The grand total price gross of tax
 * for LineItemCtnr, in purchase currency. Total prices represent the sum of
 * product prices, services prices and adjustments. (Decimal price)
 * @param {string} payload.orderNumber The number of the new order
 * @param {string} payload.totalShippingCost The shipping total price. If
 * the line item container is based on net pricing the shipping total net
 * price is returned. If the line item container is based on gross pricing
 * the shipping total gross price is returned. (Decimal price)
 * @param {string} payload.totalTax The grand total tax for LineItemCtnr,
 * in purchase currency. Total prices represent the sum of product prices,
 * services prices and adjustments. (Decimal price)
 *
 * @returns {void} No return. It pushes the trackConversion event.
 * @see https://help.salesforce.com/s/articleView?id=sf.mc_ctc_track_conversion.htm
 */
async function handlePurchasedEvent(payload) {
    pushEvent('trackConversion', {
        cart: payload.items.map(toCollectCartItem),
        // Optional parameters
        orderNumber: payload.orderNumber,
        // discount: undefined, Unknown value and format
        shipping: payload.totalShippingCost
    });
}

/**
 * Trigger a collect.js trackCart event without cart info, using the backup
 * @param {string} userUniqueId Email for the just-identified user
 */
function trackAnonymousCartAfterUserIdentification(userUniqueId) {
    const cart = anonymousCart.get();
    if (cart) {
        // Trigger trackCart only if there a previous cart with items
        handleCartEvent({ cart }, {
            userUniqueId
        });
        anonymousCart.remove();
    }
}

/**
 * Subscriptions to internal events to send the tracking to SFMC
 */
function subscribeToEvents() {
    analyticsEventCenter.subscribe(
        [
            analyticsEventCenter.EVENTS.productAddedToCart,
            analyticsEventCenter.EVENTS.productRemovedFromCart
        ],
        handleCartEvent
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.pageviewLoaded,
        handlePageviewLoadedEvent
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.purchased,
        handlePurchasedEvent
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalContactReady,
        /**
         * Handle the event triggered after submitting the abandoned cart modal
         * form, once a valid contact has been resolved in SFMC (either newly
         * created or retrieved if it already existed).
         *
         * This event guarantees that a contactKey is available and
         * associated with the provided email.
         *
         * @param {Object} payload The payload of the event
         * @param {string} payload.contactKey The unique identifier of the related
         *   contact
         * @param {string} payload.email The email address provided by the guest
         *   user
         */
        (payload) => {
            trackAnonymousCartAfterUserIdentification(payload.contactKey);
        }
    );
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.userLoggedIn,
        /**
         * Handle the userLoggedIn event
         * @param {Object} payload The payload of the event
         * @param {Object} payload.authenticatedCustomer The customer info object
         * @param {string} payload.authenticatedCustomer.ID The ID of the customer
         * @param {string} payload.authenticatedCustomer.email The email of the
         *  just logged customer
         */
        async (payload) => {
            const email = payload.authenticatedCustomer.email;
            const userUniqueId = await getContactKeyForEmailAddress(email);
            trackAnonymousCartAfterUserIdentification(userUniqueId);
        }
    );
}

/**
 * Load collect.js lib and initialize AD client
 */
function init() {
    const collectEnabled = BASE_CONFIG.sfmc.collectEnabled;
    if (!collectEnabled) return;

    _etmc = loadCollectJs(MID);
    subscribeToEvents();
}

module.exports = {
    init
};
